import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, CheckCircle, PenNib, XCircle, Question } from "@phosphor-icons/react";
import { downloadBlob } from "../components/hs/PdfBrandingDialog";
import HseSectionEditor from "../components/hse/HseSectionEditor";
import { fmtDate, fmtDateTime } from "../lib/dateUtil";

/** Phone-first reader for an approved HSE document: read it, then confirm by typing your name. */
export default function OperativeDocumentPage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const [gone, setGone] = useState(false);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);

  const load = useCallback(() => API.get(`/in-house-portal/documents/${id}`).then(({ data }) => setDoc(data)).catch(e => { if (e?.response?.status === 404) setGone(true); else toast.error(formatApiError(e)); }), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onScroll = () => { if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 160) setAtEnd(true); };
    onScroll(); window.addEventListener("scroll", onScroll); return () => window.removeEventListener("scroll", onScroll);
  }, [doc]);

  if (gone) return (
    <div className="p-6 max-w-xl mx-auto text-center space-y-3" data-testid="operative-doc-gone">
      <p className="text-base font-semibold">This document isn't current any more</p>
      <p className="text-sm text-muted-foreground">It has been replaced by a newer version or withdrawn. Anything you still need to read is listed on My Work.</p>
      <Button asChild variant="outline"><Link to="/my-work" data-testid="operative-doc-gone-back"><ArrowLeft size={14} className="mr-1" /> Back to My Work</Link></Button>
    </div>
  );
  if (!doc) return <div className="p-6 text-sm text-muted-foreground" data-testid="operative-doc-loading">Loading document…</div>;

  const quiz = doc.quiz?.enabled ? doc.quiz : null;
  const allAnswered = !quiz || quiz.questions.every(q => Number.isInteger(answers[q.id]));
  const ack = async () => {
    setBusy(true);
    try {
      await API.post(`/in-house-portal/documents/${id}/acknowledge`, { signature_name: sig, answers });
      toast.success("Thanks — your acknowledgement is recorded"); setResults(null); load(); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const body = e?.response?.data;
      if (body?.quiz_failed) { setResults(body.results); setAnswers(a => Object.fromEntries(Object.entries(a).filter(([k]) => body.results.find(r => r.id === k)?.correct))); toast.error(body.detail); }
      else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  const pdf = async () => { try { const resp = await API.get(`/in-house-portal/documents/${id}/pdf`, { responseType: "blob" }); downloadBlob(resp.data, `${doc.reference}_v${doc.version}.pdf`); } catch { toast.error("PDF failed"); } };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4 pb-36 md:pb-6" data-testid="operative-document-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/my-work" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="operative-doc-back"><ArrowLeft size={14} /> My Work</Link>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={pdf} data-testid="operative-doc-pdf"><FilePdf size={14} className="mr-1" /> PDF</Button>
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-2 text-xs"><span className="font-semibold text-muted-foreground">{doc.reference} · v{doc.version}</span><Badge className="text-[10px] bg-emerald-100 text-emerald-800">approved</Badge><span className="text-muted-foreground">{doc.type_label}</span></div>
        <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] leading-tight mt-1" data-testid="operative-doc-title">{doc.title}</h1>
        <p className="text-xs text-muted-foreground mt-1">Approved by {doc.approved_by_name}{doc.approved_by_role ? ` (${doc.approved_by_role})` : ""} on {fmtDate(doc.approved_at)}{doc.review_date ? ` · next review ${fmtDate(doc.review_date)}` : ""}</p>
      </div>

      {doc.acknowledged_at ? (
        <div className="bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/20 rounded-lg p-4 text-sm flex items-start gap-2" data-testid="operative-doc-acked">
          <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
          <div>You read and acknowledged this version on {fmtDateTime(doc.acknowledged_at)}, signed <span className="font-semibold">{doc.my_signature}</span>.{doc.quiz?.enabled ? " Understanding check passed." : ""}</div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 dark:bg-amber-950/20 rounded-lg p-3 text-xs" data-testid="operative-doc-todo">Please read the whole document. At the end, {quiz ? `answer ${quiz.questions.length} quick questions and ` : ""}type your name to confirm you have read and understood it.</div>
      )}

      <HseSectionEditor doc={doc} editable={false} onChange={() => {}} />

      {!doc.acknowledged_at && quiz && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-4" data-testid="operative-doc-quiz">
          <div>
            <p className="text-sm font-semibold flex items-center gap-2"><Question size={16} /> Quick check — {quiz.questions.length} question{quiz.questions.length === 1 ? "" : "s"}</p>
            <p className="text-xs text-muted-foreground">All {quiz.questions.length} need to be right before you can sign. If one is wrong we'll tell you which part to re-read.{quiz.attempts > 0 ? ` Attempt ${quiz.attempts + 1}.` : ""}</p>
          </div>
          {quiz.questions.map((q, i) => {
            const r = results?.find(x => x.id === q.id);
            return (
              <div key={q.id} className={`space-y-1.5 rounded-lg p-3 border ${r ? (r.correct ? "border-emerald-300 bg-emerald-50/50" : "border-red-300 bg-red-50/50") : "border-border"}`} data-testid={`operative-doc-q-${i}`}>
                <p className="text-sm font-medium">{i + 1}. {q.question}</p>
                <div className="grid gap-1.5">
                  {q.options.map((o, k) => (
                    <button key={k} type="button" onClick={() => { setAnswers(a => ({ ...a, [q.id]: k })); setResults(rs => rs ? rs.filter(x => x.id !== q.id) : rs); }} disabled={r?.correct}
                      className={`text-left text-sm rounded-md border px-3 py-2 transition-colors ${answers[q.id] === k ? "border-primary bg-primary/10 font-medium" : "border-border hover:bg-muted"}`} data-testid={`operative-doc-q-${i}-opt-${k}`}>
                      <span className="font-mono text-xs text-muted-foreground mr-2">{String.fromCharCode(65 + k)}</span>{o}
                    </button>
                  ))}
                </div>
                {r && (r.correct
                  ? <p className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Correct</p>
                  : <p className="text-xs text-red-700" data-testid={`operative-doc-q-${i}-wrong`}><XCircle size={13} weight="fill" className="inline align-text-bottom mr-1" />Not quite — re-read {r.section ? <span className="font-semibold">“{r.section}”</span> : "the document"} and pick again</p>)}
              </div>
            );
          })}
        </div>
      )}

      {!doc.acknowledged_at && (
        <div className={`bg-card border rounded-lg p-4 space-y-3 ${atEnd ? "border-primary" : "border-border"}`} data-testid="operative-doc-sign">
          <p className="text-sm font-semibold flex items-center gap-2"><PenNib size={16} /> I have read and understood this document</p>
          <p className="text-xs text-muted-foreground">Type your full name exactly as it appears on your account. This is recorded with the date and time as your signature.</p>
          <div className="flex flex-wrap gap-2">
            <Input value={sig} onChange={e => setSig(e.target.value)} placeholder="Your full name" className="h-10 flex-1 min-w-[200px]" data-testid="operative-doc-signature" />
            <Button className="h-10" onClick={ack} disabled={busy || !sig.trim() || !allAnswered} data-testid="operative-doc-ack-btn">{busy ? "Saving…" : quiz ? "Check answers & sign" : "Sign & acknowledge"}</Button>
          </div>
          {quiz && !allAnswered && <p className="text-xs text-amber-700" data-testid="operative-doc-answer-all">Answer all {quiz.questions.length} questions above first.</p>}
        </div>
      )}
    </div>
  );
}
